import { useEffect, useMemo, useState } from "react";
import { Link, createFileRoute, useNavigate } from "@tanstack/react-router";
import { Plus, ReceiptText, RefreshCw } from "lucide-react";
import { useTranslation } from "react-i18next";
import { DianReadinessBanner } from "@/components/companies/DianReadinessBanner";
import { ErrorState } from "@/components/ErrorState";
import { AppShell } from "@/components/layout/AppShell";
import { PosDocumentListFilters } from "@/components/pos-documents/PosDocumentListFilters";
import { usePosDocumentColumns } from "@/components/pos-documents/pos-document-columns";
import { RequireAuth } from "@/components/RequireAuth";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { ServerDataTable, TableSkeleton } from "@/components/ui/server-data-table";
import { useServerTable } from "@/components/ui/use-server-table";
import { usePosDocuments } from "@/lib/api/pos-documents.queries";
import type { PosDocumentStatusFilter } from "@/lib/api/pos-documents";
import { useResolutions } from "@/lib/api/resolutions.queries";
import { useActiveCompany } from "@/lib/company-context";

export const Route = createFileRoute("/companies/$companyId/pos/")({
  component: PosDocumentsPage,
});

function PosDocumentsPage() {
  return (
    <RequireAuth>
      <AppShell>
        <PosDocumentsContent />
      </AppShell>
    </RequireAuth>
  );
}

function PosDocumentsContent() {
  const { t } = useTranslation();
  const { companyId } = Route.useParams();
  const navigate = useNavigate();
  const { setActiveCompanyId } = useActiveCompany();
  const [q, setQ] = useState("");
  const [status, setStatus] = useState<PosDocumentStatusFilter>("all");
  const [periodFrom, setPeriodFrom] = useState("");
  const [periodTo, setPeriodTo] = useState("");
  const resolutions = useResolutions(companyId, {
    documentKind: "pos_electronico",
    status: "all",
  });
  const resolutionEnvironments = useMemo(
    () =>
      new Map(
        (resolutions.data?.resolutions ?? []).map((resolution) => [
          resolution.id,
          resolution.environment,
        ]),
      ),
    [resolutions.data],
  );
  const columns = usePosDocumentColumns(resolutionEnvironments);
  const table = useServerTable({
    defaultSort: { id: "emitDate", desc: true },
    defaultPageSize: 50,
    filterKey: JSON.stringify({ q, status, periodFrom, periodTo }),
  });

  useEffect(() => {
    setActiveCompanyId(companyId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  const query = usePosDocuments(companyId, {
    ...(q ? { q } : {}),
    status,
    ...(periodFrom ? { periodFrom } : {}),
    ...(periodTo ? { periodTo } : {}),
    ...table.params,
  });
  const hasFilters = q.length > 0 || status !== "all" || !!periodFrom || !!periodTo;
  const pagination = query.data?.pagination;

  return (
    <div className="mx-auto flex max-w-6xl flex-col gap-6">
      <DianReadinessBanner companyId={companyId} documentKind="pos_electronico" />
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <Link
            to="/companies/$companyId"
            params={{ companyId }}
            className="text-xs text-muted-foreground hover:text-foreground"
          >
            {t("pos.list.back")}
          </Link>
          <h1 className="mt-1 text-2xl font-semibold tracking-tight">
            {t("pos.list.title")}
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">{t("pos.list.subtitle")}</p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            disabled={query.isFetching}
            onClick={() => void query.refetch()}
          >
            <RefreshCw className={query.isFetching ? "h-4 w-4 animate-spin" : "h-4 w-4"} />
            {t("pos.list.refresh")}
          </Button>
          <Button asChild>
            <Link to="/companies/$companyId/pos/new" params={{ companyId }}>
              <Plus className="h-4 w-4" />
              {t("pos.list.new")}
            </Link>
          </Button>
        </div>
      </div>

      <PosDocumentListFilters
        q={q}
        onQChange={setQ}
        status={status}
        onStatusChange={setStatus}
        periodFrom={periodFrom}
        onPeriodFromChange={setPeriodFrom}
        periodTo={periodTo}
        onPeriodToChange={setPeriodTo}
      />

      {query.isPending ? (
        <TableSkeleton columns={columns} />
      ) : query.isError ? (
        <ErrorState
          title={t("pos.list.error_title")}
          description={t("pos.list.error_body")}
          error={query.error}
          onRetry={() => void query.refetch()}
          retryLabel={t("common.retry")}
        />
      ) : query.data.documents.length === 0 ? (
        <EmptyState companyId={companyId} hasFilters={hasFilters} />
      ) : (
        <ServerDataTable
          columns={columns}
          data={query.data.documents}
          pageCount={pagination?.totalPages ?? 1}
          rowCount={pagination?.total ?? 0}
          sorting={table.sorting}
          onSortingChange={table.onSortingChange}
          pagination={table.pagination}
          onPaginationChange={table.onPaginationChange}
          getRowId={(document) => document.id}
          onRowClick={(document) =>
            void navigate({
              to: "/companies/$companyId/pos/$documentId",
              params: { companyId, documentId: document.id },
            })
          }
          isFetching={query.isFetching}
        />
      )}
    </div>
  );
}

function EmptyState({ companyId, hasFilters }: { companyId: string; hasFilters: boolean }) {
  const { t } = useTranslation();
  return (
    <Card>
      <CardContent className="flex flex-col items-center gap-4 py-16 text-center">
        {!hasFilters ? (
          <div className="flex h-16 w-16 items-center justify-center rounded-full bg-primary/10">
            <ReceiptText className="h-8 w-8 text-primary" />
          </div>
        ) : null}
        <div className="max-w-sm space-y-1">
          <h2 className="text-lg font-semibold">
            {t(hasFilters ? "pos.list.empty_search_title" : "pos.list.empty_title")}
          </h2>
          <p className="text-sm text-muted-foreground">
            {t(hasFilters ? "pos.list.empty_search_body" : "pos.list.empty_body")}
          </p>
        </div>
        {!hasFilters ? (
          <Button asChild>
            <Link to="/companies/$companyId/pos/new" params={{ companyId }}>
              <Plus className="h-4 w-4" />
              {t("pos.list.new")}
            </Link>
          </Button>
        ) : null}
      </CardContent>
    </Card>
  );
}
